<template>
  <HrPageLayout
    title="My Retirement"
    subtitle="Upcoming retirement notice, terminal leave, and multi-department clearance"
    :breadcrumb-items="[
      { label: 'Dashboard', route: '/employee-dashboard' },
      { label: 'My Retirement' }
    ]"
  >
    <div v-if="loading" class="mr-loading">Loading retirement status…</div>
    <div v-else-if="loadError" class="mr-alert mr-alert--error">{{ loadError }}</div>
    <div v-else class="mr-wrap">
      <section class="mr-hero">
        <div>
          <p class="mr-kicker">Self-service</p>
          <h2 class="mr-title">Retirement readiness</h2>
          <p class="mr-lead">
            Your expected retirement date is calculated from date of birth and the county retirement age
            ({{ policy.retirement_age_years || '—' }} years).
          </p>
        </div>
        <div class="mr-date-card">
          <span class="mr-date-card__label">Expected retirement</span>
          <strong class="mr-date-card__value">{{ status.retirement_date || '—' }}</strong>
          <span class="mr-date-card__meta" v-if="status.days_to_retirement != null">
            <template v-if="status.days_to_retirement < 0">Past due</template>
            <template v-else-if="status.days_to_retirement === 0">Today</template>
            <template v-else>{{ status.days_to_retirement }} calendar day(s) remaining</template>
          </span>
        </div>
      </section>

      <section class="mr-grid">
        <article class="mr-card">
          <h3>Notification period</h3>
          <p>
            Staff are notified within
            <strong>{{ policy.staff_notice_days_before || '—' }}</strong> calendar days before retirement.
          </p>
          <p class="mr-meta">
            Notice sent:
            <strong>{{ (status.notice && status.notice.notification_sent_at) || 'Not yet' }}</strong>
          </p>
        </article>
        <article class="mr-card">
          <h3>Terminal leave</h3>
          <p>
            Auto-initiated within
            <strong>{{ policy.terminal_leave_days_before || '—' }}</strong> days before retirement
            (≈ <strong>{{ policy.terminal_leave_days_granted || '—' }}</strong> day(s)).
          </p>
          <template v-if="status.terminal_leave">
            <p class="mr-meta">
              <span class="mr-badge">{{ status.terminal_leave.status || 'Initiated' }}</span>
              {{ status.terminal_leave.terminal_leave_start }} → {{ status.terminal_leave.terminal_leave_end }}
              ({{ status.terminal_leave.days_granted }} day(s))
            </p>
          </template>
          <p v-else class="mr-meta">Not initiated yet.</p>
        </article>
        <article class="mr-card">
          <h3>Self-service window</h3>
          <p>
            Within
            <strong>{{ policy.ess_action_window_days || '—' }}</strong> calendar days before retirement you may
            submit clearance items to departments for action.
          </p>
          <p class="mr-meta">
            Window status:
            <strong>{{ status.in_ess_window ? 'Open' : 'Closed' }}</strong>
          </p>
        </article>
      </section>

      <section class="mr-card mr-card--wide">
        <header class="mr-card__head">
          <div>
            <h3>Department clearance</h3>
            <p v-if="status.clearance">
              Status: <span class="mr-badge">{{ status.clearance.status }}</span>
              <span v-if="status.clearance.window_start" class="mr-meta">
                · Window {{ status.clearance.window_start }} – {{ status.clearance.window_end }}
              </span>
            </p>
            <p v-else class="mr-meta">
              Clearance opens automatically when you enter the self-service window (or when HR runs automation).
            </p>
          </div>
          <div class="mr-actions" v-if="status.clearance && status.can_submit_clearance">
            <button type="button" class="mr-btn mr-btn--primary" :disabled="submitting" @click="submitAll">
              {{ submitting ? 'Submitting…' : 'Submit all pending to departments' }}
            </button>
          </div>
        </header>

        <div v-if="steps.length" class="mr-table-wrap">
          <table class="mr-table">
            <thead>
              <tr>
                <th>Department / office</th>
                <th>Status</th>
                <th>You submitted</th>
                <th>Actioned by</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="s in steps" :key="s.code">
                <td>
                  <strong>{{ s.label }}</strong>
                  <div v-if="s.employee_notes" class="mr-meta">Note: {{ s.employee_notes }}</div>
                </td>
                <td><span class="mr-badge" :class="badgeClass(s.status)">{{ s.status }}</span></td>
                <td>{{ s.employee_submitted_at || '—' }}</td>
                <td>
                  <div>{{ s.actioned_by || '—' }}</div>
                  <div class="mr-meta">{{ s.actioned_at || '' }}</div>
                </td>
                <td>
                  <button
                    v-if="canSubmitStep(s)"
                    type="button"
                    class="mr-btn mr-btn--ghost"
                    :disabled="submitting"
                    @click="submitStep(s)"
                  >
                    Submit for action
                  </button>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
        <p v-else class="mr-meta">No clearance steps yet.</p>

        <div v-if="status.clearance && status.can_submit_clearance" class="mr-notes">
          <label for="clr-notes">Notes for departments (optional)</label>
          <textarea id="clr-notes" v-model="notes" rows="2" class="mr-textarea" placeholder="e.g. library books returned on …"></textarea>
        </div>
      </section>

      <section class="mr-card mr-card--wide">
        <header class="mr-card__head">
          <div>
            <h3>Retirement forms &amp; documents</h3>
            <p class="mr-meta">
              Upload completed retirement / clearance forms (PDF, images). Files are stored under your documents as
              <strong>Retirement forms</strong>. You can also manage them under My Documents.
            </p>
          </div>
        </header>
        <div class="mr-upload-row">
          <input
            ref="retireFile"
            type="file"
            multiple
            accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
            class="mr-file"
            @change="onFilesPicked"
          >
          <button
            type="button"
            class="mr-btn mr-btn--primary"
            :disabled="uploading || !pendingFiles.length"
            @click="uploadRetirementForms"
          >
            {{ uploading ? 'Uploading…' : 'Upload forms' }}
          </button>
        </div>
        <ul v-if="pendingFiles.length" class="mr-file-list">
          <li v-for="(f, i) in pendingFiles" :key="i">{{ f.name }} ({{ Math.round(f.size / 1024) }} KB)</li>
        </ul>
        <p v-if="uploadMsg" class="mr-meta" :class="{ 'mr-ok': uploadOk }">{{ uploadMsg }}</p>
        <p class="mr-meta">
          <router-link to="/employee-documents-upload">Open My Documents · Upload</router-link>
          ·
          <router-link to="/employee-documents-view">View documents</router-link>
        </p>
      </section>
    </div>
  </HrPageLayout>
</template>

<script>
import HrPageLayout from "@/components/hrActions/HrPageLayout.vue";
import { execute, authUrl, toast } from "@/api";

export default {
  name: "MyRetirement",
  components: { HrPageLayout },
  data() {
    return {
      loading: true,
      loadError: "",
      status: {},
      notes: "",
      submitting: false,
      pendingFiles: [],
      uploading: false,
      uploadMsg: "",
      uploadOk: false
    };
  },
  computed: {
    policy() {
      return this.status.policy || {};
    },
    steps() {
      return (this.status.clearance && this.status.clearance.steps) || [];
    }
  },
  mounted() {
    this.load();
  },
  methods: {
    badgeClass(status) {
      const s = String(status || "").toLowerCase();
      if (s === "cleared") return "mr-badge--ok";
      if (s === "submitted") return "mr-badge--warn";
      if (s === "rejected") return "mr-badge--bad";
      return "";
    },
    canSubmitStep(s) {
      if (!this.status.can_submit_clearance) return false;
      const st = String(s.status || "").toLowerCase();
      return st === "pending" || st === "rejected";
    },
    load() {
      this.loading = true;
      this.loadError = "";
      const fd = new FormData();
      fd.append("function", "getMyRetirementStatus");
      execute(fd, authUrl)
        .then((res) => {
          if (res?.data?.success) {
            this.status = res.data.data || {};
          } else {
            this.loadError = res?.data?.message || "Could not load retirement status.";
          }
        })
        .catch(() => {
          this.loadError = "Could not load retirement status.";
        })
        .finally(() => {
          this.loading = false;
        });
    },
    submitStep(s) {
      this.doSubmit({ step_code: s.code, submit_all: false });
    },
    submitAll() {
      this.doSubmit({ submit_all: true });
    },
    doSubmit({ step_code, submit_all }) {
      this.submitting = true;
      const fd = new FormData();
      fd.append("function", "submitMyRetirementClearance");
      if (submit_all) fd.append("submit_all", "1");
      if (step_code) fd.append("step_code", step_code);
      if (this.notes) fd.append("notes", this.notes);
      execute(fd, authUrl)
        .then((res) => {
          if (res?.data?.success) {
            this.status = res.data.data || this.status;
            if (toast && toast.success) toast.success(res.data.message || "Submitted.");
            else alert(res.data.message || "Submitted.");
          } else if (toast && toast.error) toast.error(res?.data?.message || "Submit failed");
          else alert(res?.data?.message || "Submit failed");
        })
        .catch(() => {
          if (toast && toast.error) toast.error("Submit failed");
          else alert("Submit failed");
        })
        .finally(() => {
          this.submitting = false;
        });
    },
    onFilesPicked(e) {
      const files = Array.from(e.target.files || []);
      this.pendingFiles = files;
      this.uploadMsg = "";
    },
    async uploadRetirementForms() {
      if (!this.pendingFiles.length) return;
      this.uploading = true;
      this.uploadMsg = "";
      this.uploadOk = false;
      let ok = 0;
      let fail = 0;
      for (const file of this.pendingFiles) {
        try {
          const fd = new FormData();
          fd.append("function", "uploadEmployeeDocument");
          fd.append("file", file);
          fd.append("documentType", "Retirement forms");
          fd.append("documentName", file.name.replace(/\.[^.]+$/, "") || "Retirement form");
          fd.append("documentNumber", "");
          fd.append("validTill", "");
          const res = await execute(fd, authUrl);
          if (res?.data?.success) ok++;
          else fail++;
        } catch (e) {
          fail++;
        }
      }
      this.uploading = false;
      this.uploadOk = fail === 0 && ok > 0;
      this.uploadMsg = fail
        ? `Uploaded ${ok}, failed ${fail}. Check file type/size (PDF, JPG, PNG, DOC/DOCX).`
        : `Uploaded ${ok} form(s) successfully. View under My Documents.`;
      if (ok) {
        this.pendingFiles = [];
        if (this.$refs.retireFile) this.$refs.retireFile.value = "";
      }
      if (this.uploadOk && toast && toast.success) toast.success(this.uploadMsg);
      else if (fail && toast && toast.error) toast.error(this.uploadMsg);
    }
  }
};
</script>

<style scoped>
.mr-wrap { display: flex; flex-direction: column; gap: 1.25rem; }
.mr-loading { padding: 2rem; color: #5b6570; }
.mr-alert { padding: 1rem 1.25rem; border-radius: 8px; }
.mr-alert--error { background: #fef2f2; color: #991b1b; }
.mr-hero {
  display: flex;
  flex-wrap: wrap;
  gap: 1.5rem;
  justify-content: space-between;
  align-items: flex-end;
  padding: 1.5rem 0 0.5rem;
  border-bottom: 1px solid #e5e7eb;
}
.mr-kicker { text-transform: uppercase; letter-spacing: 0.06em; font-size: 0.75rem; color: #6b7280; margin: 0 0 0.35rem; }
.mr-title { margin: 0 0 0.5rem; font-size: 1.5rem; color: #0f172a; }
.mr-lead { margin: 0; max-width: 40rem; color: #4b5563; }
.mr-date-card {
  min-width: 12rem;
  padding: 1rem 1.25rem;
  background: linear-gradient(145deg, #0f4c81 0%, #1a6fb5 100%);
  color: #fff;
  border-radius: 12px;
}
.mr-date-card__label { display: block; font-size: 0.75rem; opacity: 0.85; }
.mr-date-card__value { display: block; font-size: 1.35rem; margin: 0.25rem 0; }
.mr-date-card__meta { font-size: 0.85rem; opacity: 0.9; }
.mr-grid { display: grid; grid-template-columns: repeat(auto-fit, minmax(220px, 1fr)); gap: 1rem; }
.mr-card {
  background: #fff;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  padding: 1.1rem 1.25rem;
}
.mr-card--wide { padding: 1.25rem 1.5rem; }
.mr-card h3 { margin: 0 0 0.5rem; font-size: 1.05rem; }
.mr-card p { margin: 0 0 0.5rem; color: #4b5563; font-size: 0.95rem; }
.mr-card__head {
  display: flex;
  flex-wrap: wrap;
  justify-content: space-between;
  gap: 1rem;
  margin-bottom: 1rem;
}
.mr-meta { font-size: 0.85rem; color: #6b7280; }
.mr-ok { color: #065f46; }
.mr-badge {
  display: inline-block;
  padding: 0.15rem 0.55rem;
  border-radius: 999px;
  background: #e5e7eb;
  color: #111827;
  font-size: 0.78rem;
  font-weight: 600;
}
.mr-badge--ok { background: #d1fae5; color: #065f46; }
.mr-badge--warn { background: #fef3c7; color: #92400e; }
.mr-badge--bad { background: #fee2e2; color: #991b1b; }
.mr-table-wrap { overflow-x: auto; }
.mr-table { width: 100%; border-collapse: collapse; font-size: 0.9rem; }
.mr-table th, .mr-table td { padding: 0.65rem 0.5rem; border-bottom: 1px solid #e5e7eb; text-align: left; vertical-align: top; }
.mr-table th { font-size: 0.78rem; text-transform: uppercase; letter-spacing: 0.04em; color: #6b7280; }
.mr-actions { display: flex; gap: 0.5rem; align-items: center; }
.mr-btn {
  border: 1px solid #cbd5e1;
  background: #fff;
  border-radius: 8px;
  padding: 0.45rem 0.85rem;
  cursor: pointer;
  font-size: 0.88rem;
}
.mr-btn:disabled { opacity: 0.6; cursor: not-allowed; }
.mr-btn--primary { background: #0f4c81; border-color: #0f4c81; color: #fff; }
.mr-btn--ghost { background: transparent; }
.mr-notes { margin-top: 1rem; }
.mr-notes label { display: block; font-size: 0.85rem; margin-bottom: 0.35rem; color: #374151; }
.mr-textarea {
  width: 100%;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  padding: 0.6rem 0.75rem;
  font: inherit;
}
.mr-upload-row {
  display: flex;
  flex-wrap: wrap;
  gap: 0.75rem;
  align-items: center;
  margin-bottom: 0.75rem;
}
.mr-file { font-size: 0.85rem; max-width: 100%; }
.mr-file-list {
  margin: 0 0 0.75rem;
  padding-left: 1.2rem;
  font-size: 0.85rem;
  color: #374151;
}
</style>
